<template>
    <div>
        <div v-for="(definition, name) in parameters">
            <div class="card shadow mb-4">
                <div class="card-header">
                    {{ $t(`parameters.${name}.name`) }}
                    <div class="float-end d-flex align-items-center gap-3">
                        <button v-if="foundCount(name) > 0" type="button"
                                class="btn btn-link btn-sm p-0 text-decoration-none"
                                @click="foundValues!.show(name)">
                            <span class="fas fa-magic me-2"></span>{{ foundLabel(name) }}
                        </button>
                        <ParameterHelp :estateType="estateType" :name="name"/>
                    </div>
                </div>
                <div class="card-body">
                    <div v-if="isPrefilled(name)" class="d-flex align-items-center mb-3 rounded px-3 py-2 border-start border-3 border-info bg-info bg-opacity-25">
                        <i class="fas me-2" :class="definition.prefilledFromSources ? 'fa-magic text-primary' : 'fa-info-circle text-info'"></i>
                        <span v-if="definition.prefilledFromSources" class="text-dark small">
                            Převzato z&nbsp;<strong>podkladů</strong> &mdash; ověřte prosím správnost údaje.
                        </span>
                        <span v-else class="text-dark small">Předvyplněno z&nbsp;<strong>RÚIAN</strong> &mdash; doporučujeme ověřit správnost údaje.</span>
                        <button type="button" class="btn-close btn-close-sm ms-auto" @click="dismissPrefilledHint(name)" aria-label="OK"></button>
                    </div>
                    <div class="mb-3" v-if="$t(`descriptions.${estateType}.${name}`)">
                      {{ $t(`descriptions.${estateType}.${name}`) }}
                    </div>
                    <component :is="resolveControl(definition.type)" v-bind="definition"
                               :parameter-id="name"
                               :value="values[name]"
                               @input="valueUpdated(name, $event)"
                               @validated="valueValidated(name, $event)"
                               :key="name + clearCounter"
                               :label="$t(`parameters.${name}.name`)"
                               :validate="validate && ! validParameters[name]"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import MatrixControl from '@/components/estimates/common/controls/MatrixControl.vue';
import ParameterHelp from '@/components/estimates/common/ParameterHelp.vue';
import {Segment} from '@/composables/Segment';
import {EstimateState} from '@/model/api';
import {ParameterList, ParameterValues} from '@/model/api/parameters';
import {NetworkError} from '@/model/errors/NetworkError';
import {Estimate} from '@/model/Estimate';
import {EVENT_PARAMETERS_VALIDATED, EventBus} from '@/model/events';
import {log} from '@/model/logger';
import {OfflineManager} from '@/model/offline';
import {NotSubmittedParameters} from '@/model/offline/NotSubmittedParameters';
import {left, right} from 'fp-ts/lib/Either';
import {fromPairs, intersection, isEqual} from 'lodash-es';
import {defineComponent, inject, nextTick, onMounted, PropType, ref} from 'vue';
import {resolveControl} from '../controls/controlComponents';
import {plural} from '../photos/texts';
import {PARAMETER_FOUND_VALUES, ParameterFoundValues} from './ParameterFoundValues';
import AccessLandListControl from '../controls/AccessLandListControl.vue';
import DateControl from '../controls/DateControl.vue';
import EnumControl from '../controls/EnumControl.vue';
import FloorAreaControl from '../controls/FloorAreaControl.vue';
import MultiEnumControl from '../controls/MultiEnumControl.vue';
import MultiTextControl from '../controls/MultiTextControl.vue';
import NumberControl from '../controls/NumberControl.vue';
import NumberOrRangeControl from '../controls/NumberOrRangeControl.vue';
import ParcelNumbersControl from '../controls/ParcelNumbersControl.vue';
import ReconstructionListControl from '../controls/ReconstructionListControl.vue';
import TextControl from '../controls/TextControl.vue';

export default defineComponent({
    components: {
        DateControl,
        ParameterHelp,
        EnumControl,
        NumberControl,
        NumberOrRangeControl,
        FloorAreaControl,
        MatrixControl,
        MultiEnumControl,
        TextControl,
        ReconstructionListControl,
        AccessLandListControl,
        MultiTextControl,
        ParcelNumbersControl,
    },
    props: {
        estateType: {
            type: String,
            required: true,
        },
        parameters: {
            type: Object as PropType<ParameterList>,
            required: true,
        },
    },
    setup(props, {expose}) {

        const eventBus = inject<EventBus>('eventBus')!;
        const model = inject<Estimate<EstimateState>>('model')!;
        const offlineManager = inject<OfflineManager>('offlineManager')!;
        const notSubmittedParameters = inject<NotSubmittedParameters>('notSubmittedParameters')!;
        const foundValues = inject<ParameterFoundValues | null>(PARAMETER_FOUND_VALUES, null);

        const acceptingInput = ref(false);
        const validate = ref(false);
        const values = ref<ParameterValues>({});
        const originalValues = ref<ParameterValues>({});
        const validParameters = ref<{[parameterName: string]: boolean}>(
            fromPairs(Object.keys(props.parameters).map(name => [name, props.parameters[name].optional === true])),
        );
        const clearCounter = ref(0);
        const userModifiedParameters = ref<{[name: string]: boolean}>({});

        function passedValues(): ParameterValues {
            return fromPairs(Object.entries(props.parameters).map(([name, def]) => [name, def.value]));
        }

        function clearNotSubmittedParameters(): void {
            notSubmittedParameters.remove(Object.keys(values.value));
        }

        function comparableValue<T>(value: T): T {
            return JSON.parse(JSON.stringify(value));
        }

        // prefilled from RUIAN or taken over from the AI sources, the user should check it
        function isPrefilled(name: string): boolean {
            return (props.parameters[name]?.prefilledFromRuian === true || props.parameters[name]?.prefilledFromSources === true)
                && !userModifiedParameters.value[name];
        }

        function dismissPrefilledHint(name: string): void {
            userModifiedParameters.value = {...userModifiedParameters.value, [name]: true};
        }

        function foundCount(name: string): number {
            return foundValues?.describeDifferences(name, values.value[name] ?? null).count ?? 0;
        }

        function foundLabel(name: string): string {
            const {count, kind} = foundValues!.describeDifferences(name, values.value[name] ?? null);

            switch (kind) {
                case 'items':
                    return `v podkladech ${count} ${plural(count, 'další položka', 'další položky', 'dalších položek')}`;
                case 'parts':
                    return `v podkladech ${count} ${plural(count, 'další část', 'další části', 'dalších částí')}`;
                default:
                    return `v podkladech ${count} ${plural(count, 'jiná hodnota', 'jiné hodnoty', 'jiných hodnot')}`;
            }
        }

        function valueUpdated(parameterName: string, value: any): void {
            values.value = {...values.value, [parameterName]: value};

            if (!acceptingInput.value) {
                return;
            }

            if (value === null
                || isEqual(comparableValue(value), comparableValue(originalValues.value[parameterName]))) {
                notSubmittedParameters.remove([parameterName]);
            } else {
                notSubmittedParameters.add(parameterName, value);
            }

            if (isPrefilled(parameterName)
                && !isEqual(comparableValue(value), comparableValue(originalValues.value[parameterName]))) {
                dismissPrefilledHint(parameterName);
            }
        }

        function valueValidated(name: string, valid: boolean): void {
            validParameters.value = {
                ...validParameters.value,
                [name]: valid && (props.parameters[name].optional || values.value[name] !== null),
            };
        }

        async function submit() {
            const currentValues: ParameterValues = values.value;

            eventBus.emit(
                EVENT_PARAMETERS_VALIDATED,
                Object.fromEntries(Object.entries(currentValues).filter(([name]) => validParameters.value[name])),
            );

            if (Object.values(validParameters.value).includes(false)) {
                validate.value = true;

                log('There are invalid parameters in segment');
                return left(null);
            }

            try {
                const acknowledged = Object.keys(userModifiedParameters.value);
                const payload = acknowledged.length > 0
                    ? {...currentValues, acknowledgedPrefilledParameters: acknowledged}
                    : currentValues;
                const estimate = await model.updateParameters(payload);
                validate.value = false;
                clearNotSubmittedParameters();

                return right(estimate);
            } catch (error) {
                offlineManager.stagedParameters.stageSegment(currentValues);

                if (error instanceof NetworkError) {
                    return left(error);
                }

                log(error instanceof Error ? error.message : String(error));
                return left(null);
            }
        }

        function hasNotSubmittedChanges(): boolean {
            const changedParameters = Object.keys(notSubmittedParameters.get());

            return intersection(changedParameters, Object.keys(values.value)).length !== 0;
        }

        function clearNotSubmittedChanges(): void {
            acceptingInput.value = false;
            values.value = passedValues();
            clearNotSubmittedParameters();
            clearCounter.value++;
            nextTick(() => {
                acceptingInput.value = true;
            });
        }

        function allowsMovingOn(): boolean {
            return Object.values(props.parameters).every(p => p.value !== null || p.optional);
        }

        function beforeEnter(): void {
            acceptingInput.value = false;
            values.value = Object.assign(passedValues(), notSubmittedParameters.get());
            originalValues.value = {...values.value};
            userModifiedParameters.value = {};
            clearCounter.value++;
            nextTick(() => {
                acceptingInput.value = true;
            });
        }

        // created logic
        beforeEnter();

        onMounted(() => {
            nextTick(() => {
                acceptingInput.value = true;
            });
        });

        const segmentApi: Segment<EstimateState> = {
            hasNotSubmittedChanges,
            clearNotSubmittedChanges,
            submit,
            allowsMovingOn,
            beforeEnter,
        };

        expose(segmentApi);

        return {
            validate,
            values,
            validParameters,
            clearCounter,
            isPrefilled,
            dismissPrefilledHint,
            foundValues,
            foundCount,
            foundLabel,
            valueUpdated,
            valueValidated,
            resolveControl,
            ...segmentApi,
        };
    },
});
</script>
